import { createSlice } from '@reduxjs/toolkit';

const offlineSlice = createSlice({
  name: 'offline',
  initialState: {
    /** 'online' | 'offline' — set in Branch Settings, persisted per branch in localStorage */
    mode: 'online',
    /** Actual OS/browser network connectivity */
    isNetworkOnline: true,
    /** Controls visibility of the SyncProgressModal */
    showSyncModal: false,
    isSyncing: false,
    syncProgress: {
      stage: null,              // 'uploading' | 'cleaning' | 'done' | 'error'
      total: 0,
      processed: 0,
      synced: 0,
      failed: 0,
      conflicts: 0,
      errors: [],
      /** Non-completed orders detected during Offline Mode pre-sync check */
      nonCompletedOrders: [],
      completed: false,
      errorMessage: null,
    },
    pendingOrdersCount:    0,
    pendingKDSEventsCount: 0,
    lastSync: null,
    /** Set when unsynced local data is older than RETENTION_WARNING_DAYS */
    retentionWarning: null, // null | { ageDays: number, oldestDate: string }
  },
  reducers: {
    setMode(state, { payload }) {
      state.mode = payload;
    },
    setNetworkOnline(state, { payload }) {
      state.isNetworkOnline = payload;
    },
    setShowSyncModal(state, { payload }) {
      state.showSyncModal = payload;
    },
    setSyncing(state, { payload }) {
      state.isSyncing = payload;
    },
    setSyncProgress(state, { payload }) {
      state.syncProgress = { ...state.syncProgress, ...payload };
    },
    resetSyncProgress(state) {
      state.syncProgress = {
        stage:               null,
        total:               0,
        processed:           0,
        synced:              0,
        failed:              0,
        conflicts:           0,
        errors:              [],
        nonCompletedOrders:  [],
        completed:           false,
        errorMessage:        null,
      };
      state.isSyncing = false;
    },
    setPendingCounts(state, { payload }) {
      state.pendingOrdersCount    = payload.orders    ?? state.pendingOrdersCount;
      state.pendingKDSEventsCount = payload.kdsEvents ?? state.pendingKDSEventsCount;
    },
    setLastSync(state, { payload }) {
      state.lastSync = payload;
    },
    // Fix F: Retention warning — set when local data is older than threshold
    setRetentionWarning(state, { payload }) {
      state.retentionWarning = payload; // { ageDays, oldestDate }
    },
    clearRetentionWarning(state) {
      state.retentionWarning = null;
    },
  },
});

export const {
  setMode,
  setNetworkOnline,
  setShowSyncModal,
  setSyncing,
  setSyncProgress,
  resetSyncProgress,
  setPendingCounts,
  setLastSync,
  setRetentionWarning,
  clearRetentionWarning,
} = offlineSlice.actions;

export default offlineSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectOfflineMode          = (s) => s.offline.mode;
export const selectIsNetworkOnline      = (s) => s.offline.isNetworkOnline;
export const selectShowSyncModal        = (s) => s.offline.showSyncModal;
export const selectIsSyncing            = (s) => s.offline.isSyncing;
export const selectSyncProgress         = (s) => s.offline.syncProgress;
export const selectPendingOrdersCount   = (s) => s.offline.pendingOrdersCount;
export const selectPendingKDSCount      = (s) => s.offline.pendingKDSEventsCount;
export const selectLastSync             = (s) => s.offline.lastSync;
export const selectRetentionWarning     = (s) => s.offline.retentionWarning;
/** True when we must write locally regardless of intent */
export const selectShouldUseLocal       = (s) =>
  s.offline.mode === 'offline' || !s.offline.isNetworkOnline;
